import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight, ArrowUpRight, BarChart3, Check, Loader2, Undo2 } from 'lucide-react'
import type { AIActionCardOpen } from '@/types/ai'
import {
  canUndo,
  cardPhase,
  firstError,
  formatCountdown,
  msUntilNextChange,
  remainingUndoSeconds,
  skippedOnUndo,
  unfinishedSteps,
  type CardOp,
  type CardPhase,
  type ChatActionCard,
} from '@/lib/aiActionCard'

// ── Bhookly AI confirm card ─────────────────────────────────────────────────
// A change the copilot proposed, shown under its reply. Nothing changes until
// the staff member taps Confirm; after that Undo is offered for the minute the
// server allows (undo_until), counted down on the server's clock. State and
// clock rules live in lib/aiActionCard.ts; the requests in useCopilotChat.

/** Lines shown before "Show all". */
const COLLAPSED_LINES = 6

const primaryBtn =
  'inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[13px] font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-60'
const secondaryBtn =
  'inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-60'

/** What a screen reader hears when the card changes state. */
const PHASE_ANNOUNCEMENTS: Record<CardPhase, string> = {
  pending: '',
  expired: 'Expired',
  working: 'Working',
  done: 'Done',
  partial: 'Partly done',
  failed: "Couldn't make the change",
  undone: 'Undone',
  undo_partial: 'Partly undone',
  dismissed: 'Dismissed',
}

function Spinner() {
  return <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
}

/** Re-renders when the card's look next depends on the clock (expiry, countdown). */
function useCardClock(card: ChatActionCard): number {
  const [tick, setTick] = useState(0)
  const now = Date.now()
  const wait = msUntilNextChange(card, card.clockOffsetMs, now)
  useEffect(() => {
    if (wait === null) return
    const id = window.setTimeout(() => setTick((t) => t + 1), Math.max(wait, 0))
    return () => window.clearTimeout(id)
  }, [wait, tick])
  return now
}

export function ActionCard({
  card,
  onAct,
  onOpen,
  onAsk,
}: {
  card: ChatActionCard
  onAct: (op: CardOp) => void
  /** Opens card.open; resolves to a note when the screen can't be opened. */
  onOpen: (open: AIActionCardOpen) => Promise<string | null>
  /** Sends card.follow_up as the next message. */
  onAsk?: (prompt: string) => void
}) {
  const now = useCardClock(card)
  const [expanded, setExpanded] = useState(false)
  const [openNote, setOpenNote] = useState<string | null>(null)
  const body = card.card ?? { title: '', lines: [], undo_available: false, domains: [] }
  const lines = body.lines ?? []
  const shown = expanded ? lines : lines.slice(0, COLLAPSED_LINES)
  const phase = cardPhase(card, card.clockOffsetMs, now)
  const undoSeconds = remainingUndoSeconds(card, card.clockOffsetMs, now)
  const busy = card.busy
  // An undo in flight keeps its button (spinning) even if the minute runs out.
  const undoable = busy === 'undo' || canUndo(card, card.clockOffsetMs, now)
  const title = body.title || 'Proposed change'
  const error = phase === 'failed' ? firstError(card) : undefined

  const undoButton = undoable && (
    <button
      type="button"
      onClick={() => onAct('undo')}
      disabled={!!busy}
      aria-label={`Undo this change (${undoSeconds} seconds left)`}
      className={secondaryBtn}
    >
      {busy === 'undo' ? <Spinner /> : <Undo2 className="h-3.5 w-3.5" aria-hidden />}
      {busy === 'undo' ? 'Undoing…' : <span className="tabular-nums">Undo {formatCountdown(undoSeconds)}</span>}
    </button>
  )

  return (
    <section
      aria-label={`Proposed change: ${title}`}
      className="mt-2 w-full max-w-md rounded-xl border border-border bg-background p-3 text-[13px] shadow-sm"
    >
      <h3 className="font-semibold leading-snug text-foreground">{title}</h3>
      {body.summary && <p className="mt-0.5 leading-snug text-muted-foreground">{body.summary}</p>}

      {shown.length > 0 && (
        <ul className="mt-2 space-y-1">
          {shown.map((l, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 break-words">
              <span className="text-muted-foreground">{l.label}</span>
              <span className="text-muted-foreground/60" aria-hidden>
                ·
              </span>
              {l.before ? (
                <>
                  <span className="text-muted-foreground line-through decoration-muted-foreground/50">{l.before}</span>
                  <ArrowRight className="h-3 w-3 shrink-0 self-center text-muted-foreground" aria-hidden />
                  <span className="sr-only">to</span>
                </>
              ) : null}
              <span className="font-medium text-foreground">{l.after}</span>
              {l.no_undo && (
                <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-medium text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-300">
                  can&apos;t undo
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {lines.length > COLLAPSED_LINES && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-1 text-[12px] font-medium text-primary hover:underline"
        >
          {expanded ? 'Show fewer' : `Show all ${lines.length}`}
        </button>
      )}

      {!!body.warnings?.length && (
        <ul className="mt-2 space-y-1 rounded-lg border border-amber-300/70 bg-amber-50 px-2.5 py-1.5 text-[12px] text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200">
          {body.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Announces state changes only — never the per-second countdown. */}
      <span className="sr-only" role="status">
        {PHASE_ANNOUNCEMENTS[phase]}
      </span>
      <div className="mt-2.5">
        {phase === 'pending' && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => onAct('confirm')} disabled={!!busy} className={primaryBtn}>
              {busy === 'confirm' ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden />}
              {busy === 'confirm' ? 'Confirming…' : 'Confirm'}
            </button>
            <button type="button" onClick={() => onAct('reject')} disabled={!!busy} className={secondaryBtn}>
              {busy === 'reject' && <Spinner />}
              Dismiss
            </button>
          </div>
        )}
        {phase === 'expired' && <p className="text-muted-foreground">Expired — ask again</p>}
        {phase === 'working' && (
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <Spinner />
            {card.status === 'undoing' ? 'Undoing…' : 'Making the change…'}
          </p>
        )}
        {phase === 'done' && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 font-medium text-emerald-700 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5" aria-hidden /> Done
            </span>
            {undoButton || <span className="text-muted-foreground">· Saved</span>}
          </div>
        )}
        {phase === 'partial' && (
          <div className="space-y-1.5">
            <p className="font-medium text-amber-700 dark:text-amber-400">Partly done</p>
            <ul className="space-y-0.5 text-[12px] text-muted-foreground">
              {unfinishedSteps(card).map((r, i) => (
                <li key={i}>
                  {r.label} — {r.status === 'not_run' ? 'not run' : r.error || 'failed'}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              {undoButton || <span className="text-muted-foreground">Saved</span>}
              {undoable && <span className="text-[12px] text-muted-foreground">Undo reverts what ran.</span>}
            </div>
          </div>
        )}
        {phase === 'failed' && (
          <div>
            <p className="font-medium text-rose-700 dark:text-rose-400">Couldn&apos;t make the change</p>
            {error && <p className="mt-0.5 text-[12px] text-muted-foreground">{error}</p>}
          </div>
        )}
        {phase === 'undone' && (
          <p className="inline-flex items-center gap-1 text-muted-foreground">
            <Undo2 className="h-3.5 w-3.5" aria-hidden /> Undone
          </p>
        )}
        {phase === 'undo_partial' && <UndoPartial card={card} />}
        {phase === 'dismissed' && <p className="text-muted-foreground">Dismissed</p>}
        {card.problem && <p className="mt-1.5 text-[12px] text-rose-700 dark:text-rose-400">{card.problem}</p>}
      </div>

      {card.follow_up && onAsk && (phase === 'done' || phase === 'partial') && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => card.follow_up && onAsk(card.follow_up.prompt)}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted"
          >
            <BarChart3 className="h-3.5 w-3.5 text-primary" aria-hidden />
            {card.follow_up.label}
          </button>
        </div>
      )}

      {body.open && (
        <div className="mt-2 border-t border-border pt-2">
          <button
            type="button"
            onClick={() => {
              const open = body.open
              if (!open) return
              setOpenNote(null)
              void onOpen(open).then(setOpenNote)
            }}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline"
          >
            Open {body.open.label}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </button>
          {openNote && <p className="mt-0.5 text-[12px] text-muted-foreground">{openNote}</p>}
        </div>
      )}
    </section>
  )
}

function UndoPartial({ card }: { card: ChatActionCard }) {
  const skipped = skippedOnUndo(card)
  return (
    <div>
      <p className="font-medium text-amber-700 dark:text-amber-400">Partly undone</p>
      <p className="mt-0.5 text-[12px] text-muted-foreground">
        {skipped.length
          ? `These were changed since and left as they are: ${skipped.map((r) => r.label).join(', ')}.`
          : "Some of this can't be undone and was left as it is."}
      </p>
    </div>
  )
}
